import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, grid, stack, vstack } from '@flipster/styled-system/patterns';
import webviewHandler from '@flipster/utils/webview-handler';

import { Picture, PictureProps } from '../../utils/Picture';

export const SparkRewardReward = ({ css: cssProp, rewardPicture }: { css?: Styles; rewardPicture: PictureProps }) => {
  return (
    <section className={css({ textAlign: 'center' }, cssProp)}>
      <h2 className={css({ textStyle: 'Display/XL', color: 'content.default.level-5', mb: [20, 60] })}>
        <FormattedMessage id='Autogen_common.r251126.s_What_s_the_reward' />
      </h2>
      <div className={stack({ mb: 26, gap: 8 })}>
        <a
          href='/launchpool'
          onClick={() => webviewHandler.postMessage({ action: 'link', params: { page: 'launchpool' } })}
        >
          <FirstSparkRewardRevealedBanner />
        </a>
        <a
          href='/spark/auction'
          onClick={() => webviewHandler.postMessage({ action: 'link', params: { page: 'challenge/sparks-auction' } })}
        >
          <SecondSparkRewardRevealedBanner />
        </a>
      </div>
      <div
        className={grid({
          columns: 1,
          bg: 'background.default.level-0',
          borderWidth: 1,
          borderColor: 'border.default.level-1',
          borderStyle: 'solid',
          rounded: 12,
          overflow: 'hidden',
        })}
      >
        <Picture
          className={css({
            gridRow: '1 / 2',
            gridColumn: '1 / 1',
            '& > img': {
              display: 'block',
              width: '100%',
              height: 'auto',
            },
          })}
          {...rewardPicture}
        />

        <div
          className={vstack({
            gridRow: '1 / 2',
            gridColumn: '1 / 1',
            pt: 16,
          })}
        >
          <div
            className={css({
              mb: [12, 16],
              py: 4,
              px: 16,
              rounded: [40],
              textStyle: 'Text/L/Strong',
              color: 'inverse.white',
              bg: 'background.accent.primary',
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_Sparks_reward' />
          </div>
          <h3
            className={css({
              textStyle: 'Title/M',
              color: 'content.default.level-5',
              mb: 8,
            })}
          >
            The more Sparks you <br className={css({ display: ['initial', 'none'] })} />
            earn, <br className={css({ display: ['none', 'initial'] })} />
            the larger your <br className={css({ display: ['initial', 'none'] })} />
            prize!
          </h3>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-5',
            })}
          >
            Exciting new rewards are on the way.
            <br className={css({ display: ['initial', 'none'] })} />
            Stay tuned for more updates!
          </p>
        </div>
      </div>
    </section>
  );
};

interface BannerProps extends React.PropsWithChildren {
  title: React.ReactNode;
}
const Banner = ({ title, children }: BannerProps) => {
  return (
    <div
      className={flex({
        py: 12,
        px: 16,
        flexDir: ['column', 'row'],
        alignItems: ['flex-start', 'center'],
        textAlign: 'left',
        borderRadius: 8,
        bg: 'background.default.level-3',
      })}
    >
      <span className={css({ mr: 8, color: 'content.accent.promotion', textStyle: 'Text/M/Strong' })}>{title}</span>
      <span className={css({ flexGrow: 1, color: 'content.default.level-5', textStyle: 'Text/M/Regular' })}>
        {children}
      </span>
    </div>
  );
};

const FirstSparkRewardRevealedBanner = () => {
  const t = useTranslations();
  return (
    <Banner title={t('Autogen_common.r251126.s_1st_Spark_Reward_revealed')}>
      Redeem a share of the{' '}
      <span className={css({ textStyle: 'Text/M/Underline', fontWeight: 'bold' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Launchpool' />
      </span>
    </Banner>
  );
};

const SecondSparkRewardRevealedBanner = () => {
  const t = useTranslations();
  return (
    <Banner title={t('Autogen_common.r251126.s_2nd_Spark_Reward_revealed')}>
      Participate in the{' '}
      <span className={css({ textStyle: 'Text/M/Underline', fontWeight: 'bold' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Sparks_Auction' />
      </span>
      for a chance to win massive winning prizes.
    </Banner>
  );
};
